import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Checkbox } from 'semantic-ui-react';

import LanguageService from '../../services/languageService';
import useApiService from '../../services/useApiService';
import useSelectItem from './use_select_item';

import './index.css'

const languageService = new LanguageService;

function useDidUpdateEffect(effect, deps) {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      const cleanup = effect();
      if (cleanup) {
        return () => cleanup();
      }
    }

    didMountRef.current = true;

  }, deps);
}

function DropdownItems(props) {
  const {itemClassName, onClickItem, options, selectedItems } = props;

  const getItemClassName = (value) => {
    return selectedItems.includes(value) ? `${itemClassName} active` : itemClassName;
  }

  return (
    <React.Fragment>
      {
        options.map((item)=>{
          return <Dropdown.Item
                  className={getItemClassName(item.value)}
                  onClick={onClickItem}
                  key={item.key}
                  value={item.value}
                  >
                  <Checkbox checked={selectedItems.includes(item.value)} label={item.text}/>
                </Dropdown.Item>
        })
      }
    </React.Fragment>
  )
}

function getValuesFromOptions(options) {
  return options.map(option => option.value);
}

function createOptionsFromArray(array) {
  return array.map((item)=>{
    return {
      key: item.name,
      value: item.name,
      text: item.name,
    }
  })
}

function createNonHeirarchyControlOptions(hierarchyOptions, array) {
  const nonHierarchyItems = array.filter((item)=> !hierarchyOptions.find((option)=>option.value===item.name))
  return createOptionsFromArray(nonHierarchyItems);
}

function FilterByTagsDropdown(props) {
  const { translations, resetFilters } = props;
  const [{ response: libControlTags }, getLibraryControlTags] = useApiService({ url: `/library_controls/control_tags`, defaultResponse: [] });
  const [{ response: libControlAdmins }, getLibraryControlAdmins] = useApiService({ url: `/library_controls/control_tags`, defaultResponse: []});

  const riskTagOptions = languageService.translateRiskTagOptions(translations);
  const riskTagOptionsValues = getValuesFromOptions(riskTagOptions);
  const controlTagOptions = languageService.translateControlTagOptions(translations);
  const controlTagOptionsValues = getValuesFromOptions(controlTagOptions);
  
  const libControlTagOptions = createNonHeirarchyControlOptions(controlTagOptions, libControlTags);
  const libControlTagOptionsValues = getValuesFromOptions(libControlTagOptions);

  const libControlAdminOptions = createOptionsFromArray(libControlAdmins);
  const libControlAdminOptionsValues = getValuesFromOptions(libControlAdminOptions);

  const businessAreaTagOptions = LanguageService.translateBusinessAreaTagOptions(translations);
  const businessAreaTagOptionsValues = getValuesFromOptions(businessAreaTagOptions);

  const [selectedRiskTags, selectRiskTag, filterSelectedRiskTags, resetSelectedRiskTags] = useSelectItem({ defaultValue: [], validItems: riskTagOptionsValues });
  const [selectedControlTags, selectControlTag, filterSelectedControlTags, resetSelectedControlTags] = useSelectItem({ defaultValue: [], validItems: controlTagOptionsValues  });
  const [selectedLibControlTags, selectLibControlTag, filterSelectedLibControlTags, resetSelectedLibControlTags] = useSelectItem({ defaultValue: [], validItems: libControlTagOptionsValues  });
  const [selectedLibControlAdmins, selectLibControlAdmin, filterSelectedLibControlAdmins, resetSelectedLibControlAdmins] = useSelectItem({ defaultValue: [], validItems: libControlAdminOptionsValues  });
  const [selectedBusinessAreaTags, selectBusinessAreaTags, filterSelectedBusinessAreaTags, resetSelectedBusinessAreaTags] = useSelectItem({ defaultValue: [], validItems: businessAreaTagOptionsValues  });

  const concatenatedTagOptions = riskTagOptions.concat(controlTagOptions, libControlTagOptions, libControlAdminOptions, businessAreaTagOptions);
  const selectedTags = selectedRiskTags.concat(selectedControlTags, selectedLibControlTags, selectedLibControlAdmins, selectedBusinessAreaTags);

  const onRemoveItem = (evt, data) => {
    filterSelectedRiskTags(evt, data);
    filterSelectedControlTags(evt, data);
    filterSelectedLibControlTags(evt, data)
    filterSelectedLibControlAdmins(evt, data);
    filterSelectedBusinessAreaTags(evt, data);
  }

  useEffect(()=>{
    getLibraryControlTags({ params: { context: "tags" } })
    getLibraryControlAdmins({ params: { context: "owners" } })
  }, [])

  useDidUpdateEffect(()=>{
    props.onChange({
      riskTagsFilter: selectedRiskTags,
      controlTagsFilter: selectedControlTags,
      libControlTagsFilter: selectedLibControlTags,
      libControlAdminsFilter: selectedLibControlAdmins,
      businessAreaTagsFilter: selectedBusinessAreaTags
    })
  }, [selectedRiskTags, selectedControlTags, selectedLibControlTags, selectedLibControlAdmins, selectedBusinessAreaTags])

  useEffect(()=>{
    if (resetFilters) {
      resetSelectedRiskTags([])
      resetSelectedControlTags([])
      resetSelectedLibControlTags([])
      resetSelectedLibControlAdmins([])
      resetSelectedBusinessAreaTags([])
    }
  }, [resetFilters])

  return (
    <Dropdown
      id="filter-by-tags-dropdown"
      className="filter-by-tags-dropdown"
      placeholder={props.placeholder}
      options={concatenatedTagOptions}
      value={selectedTags}
      onChange={onRemoveItem}
      onFocus={props.onFocus}
      fluid
      multiple
      search
      selection
      closeOnBlur
      closeOnChange
    >
      <Dropdown.Menu>
        <div>
          <Dropdown.Header>Risk Tags</Dropdown.Header>
          <DropdownItems
            itemClassName="risk-tag-option"
            onClickItem={selectRiskTag}
            options={riskTagOptions}
            selectedItems={selectedRiskTags}
          />
        </div>
        <div>
          <Dropdown.Header>Hierarchy Controls</Dropdown.Header>
          <DropdownItems
            itemClassName="control-tag-option"
            onClickItem={selectControlTag}
            options={controlTagOptions}
            selectedItems={selectedControlTags}
          />
        </div>
        <div>
          <Dropdown.Header>Control Tags</Dropdown.Header>
          <DropdownItems
            itemClassName="control-tag-option"
            onClickItem={selectLibControlTag}
            options={libControlTagOptions}
            selectedItems={selectedLibControlTags}
          />
        </div>
        <div>
          <Dropdown.Header>Control Administrators</Dropdown.Header>
          <DropdownItems
            itemClassName="control-tag-option"
            onClickItem={selectLibControlAdmin}
            options={libControlAdminOptions}
            selectedItems={selectedLibControlAdmins}
          />
        </div>
        <div>
          <Dropdown.Header>Business Area Identification</Dropdown.Header>
          <DropdownItems
            itemClassName="risk-tag-option"
            onClickItem={selectBusinessAreaTags}
            options={businessAreaTagOptions}
            selectedItems={selectedBusinessAreaTags}
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const mapStateToProps = (state) => {
  return {
    translations: state.language.language.translations,
  }
}

export default connect(mapStateToProps, {})(FilterByTagsDropdown);


// WEBPACK FOOTER //
// ./src/views/project_control_register/filter_by_tags_dropdown.js